import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import logger from '@widgets/logger';
import { Input } from '@components/Input';
import Popper from '@components/Popper';
import { CssNoOptions } from '@components/Select';
import { Typography } from '@components/Typography';
import { usePopupZIndex } from '@components/Modal/popupLayer';
import { useTranslation } from '@hooks';

const SUGGESTIONS_POPPER_OPTIONS = {
    placement: 'bottom-start',
    modifiers: [
        { name: 'offset', options: { offset: [0, 4] } },
        { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
    ],
};

const SuggestionsList = styled(MenuList)({
    padding: 0,
    maxHeight: '55vh',
    overflowY: 'auto',
});

const SuggestionItem = styled(MenuItem)({
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    '&.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: '#E4EAEB',
    },
});

const SuggestionText = styled(Typography)({
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const LocationAutocomplete = ({
    label,
    autocompleteRequestOptions = {},
    onSelectOption,
    onClearPlace,
    onBlur,
    initialValue = '',
    required = false,
    disabled = false,
    autoFocus = false,
    error = null,
    helperText = null,
}) => {
    const { t } = useTranslation();
    const popupZIndex = usePopupZIndex();
    const [suggestions, setSuggestions] = useState([]);
    const [search, setSearch] = useState(initialValue);
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const autocompleteService = useRef(null);
    const inputRef = useRef(null);
    const committedValueRef = useRef(initialValue);
    const requestIdRef = useRef(0);

    const loadMap = async () => {
        try {
            const { AutocompleteService } = await window.google.maps.importLibrary('places');
            autocompleteService.current = new AutocompleteService();
        } catch (error) {
            logger.error('Could not load the AutocompleteService', error);
        }
    };

    useEffect(() => {
        loadMap();
    }, []);

    const close = () => {
        requestIdRef.current += 1;
        setIsOpen(false);
        setSuggestions([]);
        setHighlightedIndex(-1);
    };

    useEffect(() => {
        setSearch(initialValue);
        committedValueRef.current = initialValue;
        close();
    }, [initialValue]);

    const selectSuggestion = suggestion => {
        setSearch(suggestion.description);
        committedValueRef.current = suggestion.description;
        close();
        onSelectOption?.(suggestion);
    };

    const onInputValueChange = inputValue => {
        if (inputValue === '') {
            onClearPlace?.();
            committedValueRef.current = '';
            close();
            return;
        }
        const requestId = ++requestIdRef.current;
        autocompleteService.current?.getPlacePredictions(
            { input: inputValue, ...autocompleteRequestOptions },
            (predictions, serviceStatus) => {
                if (requestId !== requestIdRef.current) return;
                const isOk = serviceStatus === window.google.maps.places.PlacesServiceStatus.OK;
                setSuggestions(isOk ? predictions : []);
                setHighlightedIndex(-1);
                setIsOpen(true);
            },
        );
    };

    const handleBlur = () => {
        setSearch(committedValueRef.current);
        close();
        onBlur?.();
    };

    const handleKeyDown = event => {
        if (!isOpen) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedIndex(index => Math.min(index + 1, suggestions.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex(index => Math.max(index - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (suggestions[highlightedIndex]) selectSuggestion(suggestions[highlightedIndex]);
        } else if (event.key === 'Escape') {
            setSearch(committedValueRef.current);
            close();
        }
    };

    const anchorEl = inputRef.current?.closest('.MuiInputBase-root') ?? null;

    return (
        <>
            <Input
                inputRef={inputRef}
                inputProps={{
                    value: search,
                    onChange: event => {
                        setSearch(event.target.value);
                        onInputValueChange(event.target.value);
                    },
                    onBlur: handleBlur,
                    onKeyDown: handleKeyDown,
                }}
                label={label}
                fullWidth
                required={required}
                disabled={disabled}
                autoFocus={autoFocus}
                error={!!error}
                helperText={error || helperText}
            />
            <Popper
                open={isOpen && !!anchorEl}
                anchorEl={anchorEl}
                disablePortal={false}
                popperOptions={SUGGESTIONS_POPPER_OPTIONS}
                sx={{ zIndex: popupZIndex, width: anchorEl?.clientWidth }}
                onMouseDown={event => event.preventDefault()}
            >
                <SuggestionsList>
                    {suggestions.length ? (
                        suggestions.map((suggestion, index) => (
                            <SuggestionItem
                                key={suggestion.place_id}
                                selected={index === highlightedIndex}
                                onClick={() => selectSuggestion(suggestion)}
                            >
                                <SuggestionText variant="body2">
                                    {suggestion.structured_formatting.main_text}
                                </SuggestionText>
                                <SuggestionText variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                                    {suggestion.structured_formatting.secondary_text}
                                </SuggestionText>
                            </SuggestionItem>
                        ))
                    ) : (
                        <CssNoOptions>{t('common.noOptions')}</CssNoOptions>
                    )}
                </SuggestionsList>
            </Popper>
        </>
    );
};

export default LocationAutocomplete;
